import React from 'react';
import { motion } from 'framer-motion';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';

const EmptyState = ({ 
  icon: Icon,
  title = "Nothing to see here",
  description = "This area is empty for now.",
  actionText = "Get Started",
  actionLink = "/",
  showAction = true,
  variant = "default" // default, error, success, warning
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'error':
        return {
          iconBg: 'bg-red-100',
          iconColor: 'text-red-500',
          titleColor: 'text-red-900',
          descriptionColor: 'text-red-600'
        };
      case 'success':
        return {
          iconBg: 'bg-green-100',
          iconColor: 'text-green-500',
          titleColor: 'text-green-900',
          descriptionColor: 'text-green-600'
        };
      case 'warning':
        return {
          iconBg: 'bg-yellow-100',
          iconColor: 'text-yellow-500',
          titleColor: 'text-yellow-900',
          descriptionColor: 'text-yellow-600'
        };
      default:
        return {
          iconBg: 'bg-slate-100',
          iconColor: 'text-slate-500',
          titleColor: 'text-slate-900',
          descriptionColor: 'text-slate-600'
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center py-12 px-4 text-center"
    >
      {/* Icon */}
      {Icon && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
          className={`w-20 h-20 ${styles.iconBg} rounded-full flex items-center justify-center mb-6`}
        >
          <Icon className={`w-10 h-10 ${styles.iconColor}`} />
        </motion.div>
      )}

      {/* Title */}
      <motion.h3
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className={`text-xl font-semibold ${styles.titleColor} mb-3`}
      >
        {title}
      </motion.h3>

      {/* Description */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className={`text-base ${styles.descriptionColor} mb-8 max-w-md`}
      >
        {description}
      </motion.p>

      {/* Action Button */}
      {showAction && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            component={Link}
            to={actionLink}
            variant="contained"
            color="primary"
            size="large"
            className="px-6 py-3"
          >
            {actionText}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
};

// Predefined empty states
export const EmptyCart = () => (
  <EmptyState
    icon={({ className }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
      </svg>
    )}
    title="Your cart is empty"
    description="Looks like you haven't added any products to your cart yet. Start shopping to discover amazing travel gear!"
    actionText="Start Shopping"
    actionLink="/shop"
  />
);

export const EmptyWishlist = () => (
  <EmptyState
    icon={({ className }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    )}
    title="Your wishlist is empty"
    description="Save your favorite products here to keep track of items you love. Start browsing to add some products!"
    actionText="Browse Products"
    actionLink="/shop"
  />
);

export const EmptySearch = ({ searchTerm }) => (
  <EmptyState
    icon={({ className }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    )}
    title={`No results found for "${searchTerm}"`}
    description="Try adjusting your search terms or browse our categories to find what you're looking for."
    actionText="Browse All Products"
    actionLink="/shop"
  />
);

export const EmptyOrders = () => (
  <EmptyState
    icon={({ className }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )}
    title="No orders yet"
    description="You haven't placed any orders yet. Start shopping to see your order history here!"
    actionText="Start Shopping"
    actionLink="/shop"
  />
);

export default EmptyState;
